import { supabase, Sentry, isDev, SupabaseError, withRetry } from './shared'
import { normalizeAudioSourceUrl } from '../audio-url-normalization'

// Audio URL verification cache to reduce HEAD requests (5-minute TTL)
const audioUrlVerificationCache = new Map<string, { url: string; timestamp: number }>();
const AUDIO_URL_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const PRIVATE_SUBMISSION_PATH_PREFIX = 'submissions/'
const QUARANTINE_AUDIO_PATH_PREFIX = 'quarantine-audio/'

function isBlockedPrivateAudioPath(path: string): boolean {
  const normalized = path.toLowerCase()
  return (
    normalized.startsWith(PRIVATE_SUBMISSION_PATH_PREFIX) ||
    normalized.startsWith(QUARANTINE_AUDIO_PATH_PREFIX) ||
    normalized.includes(`/${QUARANTINE_AUDIO_PATH_PREFIX}`)
  )
}

// Supabase Storage hosts are safe to serve directly to the browser (open CORS,
// Range support, allowed by the CSP media-src). Used to decide whether a full
// URL can bypass the audio proxy.
function isSupabaseHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === 'supabase.co' || host.endsWith('.supabase.co');
}

function isBlockedPrivateAudioUrl(url: URL): boolean {
  const normalizedPath = url.pathname.replace(/^\/+/, '').toLowerCase()
  return (
    normalizedPath.includes('storage/v1/object/public/audio-files/submissions/') ||
    normalizedPath.includes('storage/v1/object/public/audio-files/quarantine-audio/')
  )
}

function getCachedAudioUrl(fileUrl: string): string | null {
  const cached = audioUrlVerificationCache.get(fileUrl);
  if (cached && Date.now() - cached.timestamp < AUDIO_URL_CACHE_TTL) {
    return cached.url;
  }
  if (cached) {
    audioUrlVerificationCache.delete(fileUrl); // Remove stale entry
  }
  return null;
}

function setCachedAudioUrl(fileUrl: string, resolvedUrl: string): void {
  // Limit cache size to prevent memory issues
  if (audioUrlVerificationCache.size > 100) {
    // Remove oldest entries
    const entries = Array.from(audioUrlVerificationCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    for (let i = 0; i < 20; i++) {
      audioUrlVerificationCache.delete(entries[i][0]);
    }
  }
  audioUrlVerificationCache.set(fileUrl, { url: resolvedUrl, timestamp: Date.now() });
}

// Storage operations for audio files
export const storageApi = {
  // Get public URL for audio file.
  // Intentionally NOT wrapped in withRetry: this is cache-first (5-min TTL) and
  // pure URL resolution — no transient network call to retry; the audio element's
  // own load handles fetch failures.
  async getAudioUrl(fileUrl: string): Promise<string | null> {
    if (isDev) console.log(`[Supabase] Getting audio URL for: ${fileUrl}`);

    // A row with no `file_url` at all is a known DATA state, not a defect, so it
    // resolves to null silently (TES-1977).
    //
    // 130 rows in prod carry an empty `file_url` — the `stt_error` imports that
    // never got audio. They are all `is_active = false`, but two live paths still
    // read them and call in here: `/api/sounds/[id]`, and `/sounds/<slug>`, which
    // deliberately renders a deactivated row with a real title and `noindex`
    // rather than 404ing it (TES-652 — chain-rot self-healing). So this branch is
    // reached on ordinary crawler traffic to any of those 130 URLs.
    //
    // Reporting it per call made an expected, unbounded-frequency data condition
    // into one Sentry event per request. That is what produced ~2,400 events in
    // three days from the list route before TES-1939 filtered `is_active` there,
    // and it kept firing afterwards because the two detail paths above were never
    // covered. The right detector for "an ACTIVE sound has no audio" is the
    // check-audio-links cron over the table, not a per-request capture.
    //
    // SQL NULL (`null`/`undefined`) is the same data state as `''`. TES-1977
    // silenced the empty string but left a typeof-not-string branch reporting
    // per call, which is how TESLA-LOCK-SOUND-4N stayed on the capture path.
    // Nothing on a per-request path reports an unusable `file_url` any more
    // (TES-2116): the value is typed `string`, so a non-string is a type
    // violation the type checker owns, and the detector for "an ACTIVE sound
    // has no audio" is the check-audio-links cron over the table.
    if (typeof fileUrl !== 'string' || !fileUrl) return null;

    const normalizedFileUrl = normalizeAudioSourceUrl(fileUrl);
    if (!normalizedFileUrl) return null;

    // Check cache first to avoid redundant HEAD requests
    const cachedUrl = getCachedAudioUrl(normalizedFileUrl);
    if (cachedUrl) {
      if (isDev) console.log(`[Supabase] Using cached URL for: ${normalizedFileUrl}`);
      return cachedUrl;
    }

    // If fileUrl is a full URL, handle it appropriately
    if (normalizedFileUrl.startsWith('http://') || normalizedFileUrl.startsWith('https://')) {
      if (isDev) console.log('[Supabase] External URL detected, using audio proxy.');

      // Validate that it's a valid URL
      let parsedUrl: URL
      try {
        parsedUrl = new URL(normalizedFileUrl);
      } catch {
        // SECURITY: Don't include file URLs in Sentry logs
        Sentry.captureMessage('Invalid URL format in getAudioUrl', { level: 'warning' });
        return this.getDemoAudioUrl(normalizedFileUrl);
      }

      if (isBlockedPrivateAudioUrl(parsedUrl)) {
        Sentry.captureMessage('Blocked private audio URL in getAudioUrl', { level: 'warning' });
        return null;
      }

      // Supabase Storage serves audio with open CORS (`access-control-allow-origin: *`),
      // Range support, and correct audio/* content types, and the CSP media-src allows
      // https://*.supabase.co. So Supabase URLs are served directly to the <audio>
      // element — this keeps every play off the Vercel Function path, eliminating the
      // Fast Origin + Fast Data Transfer the proxy billed per play. The proxy is retained
      // only as a CORS fallback for any unexpected non-Supabase host.
      if (isSupabaseHost(parsedUrl.hostname)) {
        setCachedAudioUrl(normalizedFileUrl, normalizedFileUrl);
        return normalizedFileUrl;
      }

      const proxyUrl = `/api/audio-proxy?url=${encodeURIComponent(normalizedFileUrl)}`;
      setCachedAudioUrl(normalizedFileUrl, proxyUrl);
      return proxyUrl;
    }

    // Handle relative paths that might have leading slashes
    let cleanPath = normalizedFileUrl;
    if (cleanPath.startsWith('/')) {
      cleanPath = cleanPath.substring(1);
    }

    if (isBlockedPrivateAudioPath(cleanPath)) {
      Sentry.captureMessage('Blocked private audio path in getAudioUrl', { level: 'warning' });
      return null;
    }

    // Use public URL directly since the audio-files bucket is public
    // This is more reliable than createSignedUrl which requires specific RLS policies
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    if (!supabaseUrl) {
      Sentry.captureMessage('Missing NEXT_PUBLIC_SUPABASE_URL in getAudioUrl', { level: 'error' });
      return this.getDemoAudioUrl(cleanPath);
    }

    const normalizedSupabaseUrl = normalizeAudioSourceUrl(supabaseUrl).replace(/\/+$/, '');
    if (!normalizedSupabaseUrl) {
      Sentry.captureMessage('Invalid NEXT_PUBLIC_SUPABASE_URL in getAudioUrl', { level: 'error' });
      return this.getDemoAudioUrl(cleanPath);
    }

    const publicUrl = `${normalizedSupabaseUrl}/storage/v1/object/public/audio-files/${cleanPath}`;
    if (isDev) console.log(`[Supabase] Using public URL: ${publicUrl}`);

    // Serve the Supabase public URL directly to the browser. Supabase Storage
    // returns open CORS + Range support and the CSP media-src allows
    // https://*.supabase.co, so the <audio> element loads it without the proxy.
    // This keeps audio playback entirely off the Vercel Function path.
    setCachedAudioUrl(normalizedFileUrl, publicUrl);
    return publicUrl;
  },

  // Generate demo audio URL for development/demo purposes
  // Security: Only returns Supabase-hosted audio to comply with proxy whitelist
  getDemoAudioUrl(originalPath: string): string {
    if (isDev) console.log(`[Supabase] Generating demo audio URL for: ${originalPath}`);

    if (!supabase) {
      if (isDev) console.warn(`[Supabase] Client not initialized, cannot get demo audio`);
      return '';
    }

    // Use a demo file from Supabase storage if available
    // The proxy now only allows Supabase URLs for security (SSRF prevention)
    const demoPath = 'demo/demo-sound.wav';
    const { data } = supabase.storage.from('audio-files').getPublicUrl(demoPath);

    if (data?.publicUrl) {
      if (isDev) console.log(`[Supabase] Using Supabase demo audio: ${data.publicUrl}`);
      // Served directly — Supabase Storage is CORS-open and CSP-allowed.
      return data.publicUrl;
    }

    // If no demo file exists, return empty string (audio player will show unavailable state)
    if (isDev) console.warn(`[Supabase] No demo audio available for: ${originalPath}`);
    return '';
  },

  // Download file from storage
  async downloadFile(path: string): Promise<Blob> {
    try {
      return await withRetry(async () => {
        const normalizedPath = normalizeAudioSourceUrl(path)

        if (!normalizedPath) {
          throw new SupabaseError('Invalid audio file path')
        }

        // Handle different URL types for download
        if (normalizedPath.startsWith('https://')) {
          // For full URLs, fetch directly
          const response = await fetch(normalizedPath)
          if (!response.ok) {
            throw new SupabaseError(`Failed to download file: ${response.statusText}`)
          }
          return await response.blob()
        }

        // Reject user submission paths — these must never be served publicly.
        if (isBlockedPrivateAudioPath(normalizedPath)) {
          throw new SupabaseError('Cannot download a private audio path via the public client')
        }

        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        // Construct the public CDN URL client-side (getPublicUrl() makes no network
        // call) and fetch it directly. This avoids going through the RLS-checked
        // storage.objects SELECT path, which would also expose a bucket-listing vector.
        const { data: { publicUrl } } = supabase.storage
          .from('audio-files')
          .getPublicUrl(normalizedPath)

        const response = await fetch(publicUrl)
        if (!response.ok) {
          if (response.status === 404) {
            throw new SupabaseError(`Audio file not found: ${normalizedPath}`)
          }
          throw new SupabaseError(`Failed to download file: ${response.statusText}`)
        }

        return await response.blob()
      })
    } catch (error) {
      Sentry.captureException(error, { tags: { context: 'storageApi', action: 'downloadFile' } })
      throw error
    }
  },

  // Check if file exists
  async fileExists(path: string): Promise<boolean> {
    try {
      // HEAD requests and Supabase list calls transiently fail (network blips,
      // 5xx), so retry like the rest of storage.ts. A retried throw is treated as
      // a transient failure; a clean resolve (response.ok / list result) is final.
      return await withRetry(async () => {
        // For full URLs, try to fetch HEAD request
        if (path.startsWith('https://')) {
          const response = await fetch(path, { method: 'HEAD' })
          return response.ok
        }

        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        // For relative paths, check in Supabase storage
        const { data, error } = await supabase.storage
          .from('audio-files')
          .list('', { search: path })

        if (error) {
          throw new SupabaseError(`Failed to check file existence: ${error.message}`, error)
        }

        return Boolean(data && data.length > 0)
      })
    } catch (error) {
      // SECURITY: Don't include file paths in Sentry logs
      Sentry.captureException(error, { tags: { context: 'storageApi', action: 'fileExists' } })
      return false
    }
  }
}
